/**
 * Canonical sidebar menu definition — the SINGLE SOURCE OF TRUTH shared by:
 *   1. The "ตั้งค่าเมนู" settings page (src/views/Profile/MenuSettings.vue)
 *   2. The Sidebar component (owned by Dev-BO-D) for show/hide/pin filtering
 *   3. The `menuPref` vuex module (src/store/menu_pref_module.js)
 *
 * KEY CONVENTION
 * --------------
 * Each node's `key` is the SAME identifier as the computed variable name used
 * in DashboardLayout.vue (e.g. `menu_order`, `menu_dashboard_team`). These are
 * unique and stable across renames of paths/labels, so they are the contract
 * BO-D uses when wiring `menuPref/isMenuShown(key)` into each <sidebar-item>.
 *
 * NODE SHAPE
 *   key    : stable identifier (matches DashboardLayout computed name)
 *   i18n   : vue-i18n key used to render the label ($t(node.i18n))
 *   path   : route path (optional — group-only headers have none)
 *   access : array of Auth getter names; node is "accessible" if ANY is true
 *            (OR semantics, mirroring the v-if in DashboardLayout). An empty
 *            array means the item is always visible (no role gate).
 *   requireAll : optional. When true the `access` array is read as AND instead
 *            of OR — for the handful of v-ifs in DashboardLayout that are
 *            spelled `a && b` (the two ต้นทุน reports). Mirroring those with OR
 *            offers a menu on the "ตั้งค่าเมนู" page that the sidebar refuses to
 *            render, which reads as a broken permission rather than as a
 *            missing one.
 *   children : nested sub-menu items (same shape)
 */

export const MENU_TREE = [
    {
        key: "menu_dashboard",
        i18n: "menu_dashboard",
        path: "/backoffice/reports/dashboard",
        access: ["isAccessPayment", "isAccessDashboardByPlatformChannel"],
        children: [
            { key: "menu_dashboard_team", i18n: "menu_dashboard_team", path: "/backoffice/reports/dashboard", access: ["isAccessPayment"] },
            { key: "menu_dashboard_platform", i18n: "menu_dashboard_platform", path: "/backoffice/reports/dashboard-by-platform-channel", access: ["isAccessDashboardByPlatformChannel"] }
        ]
    },
    { key: "menu_order_form", i18n: "order_form", path: "/backoffice/orders/create", access: ["isAccessOrder"] },
    { key: "menu_order", i18n: "orders", path: "/backoffice/orders", access: ["isAccessOrder"] },
    // #441: MadameFin staff only — `isAccessOrder && isMdfStaff`, same as the
    // sidebar v-if. The key stays menu_order_new (saved prefs use it).
    { key: "menu_order_new", i18n: "orders_new.title", path: "/backoffice/orders-overview", access: ["isAccessOrder", "isMdfStaff", "isAccessOrderOverview"], requireAll: true },
    { key: "menu_order_reprocess", i18n: "reprocess.title", path: "/backoffice/orders/reprocess", access: ["isAdmin"] },
    { key: "menu_available_stock", i18n: "wms.menu.available_product", path: "/backoffice/available-stock", access: ["isAccessOrder"] },
    { key: "menu_payment", i18n: "payment_check", path: "/backoffice/payments", access: ["isAccessPayment"] },
    { key: "menu_packing", i18n: "packings", path: "/backoffice/packings", access: ["isAccessPacking"] },
    { key: "menu_order_return", i18n: "order_return", path: "/backoffice/packings/shipping/return", access: ["isAccessOrderReturn"] },
    { key: "menu_coupon", i18n: "coupon", path: "/backoffice/coupon", access: ["isAccessCoupon"] },
    {
        key: "menu_platform_statement",
        i18n: "platform_statement.menu",
        path: "/backoffice/platform-statement",
        access: ["isAccessPlatformStatement"],
        children: [
            { key: "menu_platform_statement_list", i18n: "platform_statement.list", path: "/backoffice/platform-statement", access: ["isAccessPlatformStatement"] },
            { key: "menu_platform_statement_upload", i18n: "platform_statement.upload", path: "/backoffice/platform-statement/upload", access: ["isAccessPlatformStatement"] },
            { key: "menu_platform_statement_summary", i18n: "platform_statement.summary", path: "/backoffice/platform-statement/summary", access: ["isAccessPlatformStatement"] }
        ]
    },
    {
        key: "menu_report",
        i18n: "reports",
        path: "/backoffice/reports",
        access: ["isAccessReport"],
        children: [
            { key: "menu_report_team", i18n: "report_by_team", path: "/backoffice/reports/team", access: ["isAccessReport"] },
            { key: "menu_report_sale_chnanel", i18n: "report_by_sale_channel", path: "/backoffice/reports/sale-channel", access: ["isAccessReport"] },
            { key: "menu_report_product", i18n: "report_by_product", path: "/backoffice/reports/product", access: ["isAccessReport"] },
            { key: "menu_report_user", i18n: "report_by_user", path: "/backoffice/reports/user", access: ["isAccessReport"] },
            { key: "menu_report_user_product", i18n: "report_by_user_product", path: "/backoffice/reports/user-product", access: ["isAccessReport"] },
            { key: "menu_report_wms_summary", i18n: "report_wms_summary", path: "/backoffice/reports/summary-wms", access: ["isAccessReport"] },
            { key: "menu_report_wms_product_summary", i18n: "report_wms_product_summary", path: "/backoffice/reports/summary-wms-product", access: ["isAccessReport"] },
            { key: "menu_report_wms_field_summary", i18n: "report_wms_field_summary", path: "/backoffice/reports/summary-wms-field", access: ["isAccessReport"] },
            // Both cost reports need isAccessReport AND canViewCost, exactly as
            // their v-ifs in DashboardLayout.vue are spelled: the ต้นทุน
            // allowlist lives in the API's config.yml (bo-api #594 §4.1) and is
            // narrower than "may see reports" — it does not replace it. Hence
            // requireAll: under plain OR semantics this file would offer the two
            // ต้นทุน menus on the "ตั้งค่าเมนู" page to anyone who may see any
            // report, while the sidebar refuses to render them. Keys are
            // appended, never reordered — menu_pref_module stores them.
            { key: "menu_report_salechannel_product", i18n: "report_by_sale_channel_and_product", path: "/backoffice/reports/salechannel-product", access: ["isAccessReport", "canViewCost"], requireAll: true },
            { key: "menu_report_order_return", i18n: "report_order_return", path: "/backoffice/reports/order-return", access: ["isAccessReport"] },
            { key: "menu_report_product_cost", i18n: "report_product_cost", path: "/backoffice/reports/product-cost", access: ["isAccessReport", "canViewCost"], requireAll: true }
        ]
    },
    { key: "menu_platform", i18n: "platform_connect", path: "/platform/tiktok", access: ["isAccessPlatform"] },
    {
        key: "menu_inventory",
        i18n: "wms.menu.inventory",
        path: "/wms/inventory",
        access: ["isAccessWms"],
        children: [
            { key: "menu_inventory_dashboard", i18n: "wms.menu.inventory_dashboard", path: "/wms/inventory/dashboard", access: ["isAccessWms"] },
            { key: "menu_inventory_list", i18n: "wms.menu.inventory_list", path: "/wms/inventory/summary", access: ["isAccessWms"] },
            { key: "menu_inventory_list_detail", i18n: "wms.menu.inventory_list_detail", path: "/wms/inventory/detail", access: ["isAccessWms"] },
            { key: "menu_inventory_history", i18n: "wms.menu.inventory_history", path: "/wms/history", access: ["isEmployee"] },
            { key: "menu_inventory_history_storage_list", i18n: "wms.menu.inventory_history_storage_list", path: "/wms/inventory/storage", access: ["isAccessWms"] },
            { key: "menu_inventory_summary_packing", i18n: "wms.menu.inventory_summary_packing", path: "/wms/inventory/packing", access: ["isAccessWms"] },
            { key: "menu_return_warehouse", i18n: "wms.menu.return_warehouse", path: "/wms/inventory/return", access: ["isAccessWms"] },
            { key: "menu_partner", i18n: "wms.menu.partner", path: "/wms/partner", access: ["isAdmin"] },
            { key: "menu_product", i18n: "wms.menu.product", path: "/wms/product", access: ["isAdmin"] },
            { key: "menu_product_wait_ship", i18n: "menu_pref.product_wait_ship", path: "/wms/product/waitship", access: ["isEmployee"] },
            { key: "menu_storage", i18n: "wms.menu.storage", path: "/wms/storage", access: ["isAdmin"] },
            // #437 — appended, never inserted: menu_pref_module stores keys by position.
            { key: "menu_inventory_shipped_no_deduct", i18n: "wms.menu.shipped_no_deduct", path: "/wms/inventory/shipped-no-deduct", access: ["isAccessWms"] }
        ]
    },
    {
        key: "menu_express",
        i18n: "menu_pref.express",
        path: "/express",
        access: ["isAccessExpress"],
        children: [
            { key: "menu_express_list", i18n: "menu_pref.express_list", path: "/express", access: ["isAccessExpress"] },
            { key: "menu_express_form", i18n: "menu_pref.express_form", path: "/wms/storage", access: ["isAccessExpress"] }
        ]
    },
    {
        key: "menu_admin",
        i18n: "menu.admin",
        path: "/admin",
        access: ["isAdmin"],
        children: [
            { key: "menu_user", i18n: "menu.user", path: "/admin/user", access: ["isAdmin"] },
            { key: "menu_platform_connect", i18n: "menu.admin_platform_connect", path: "/admin/platform", access: ["isAdmin"] },
            { key: "menu_sale_channel", i18n: "menu.sale_channel", path: "/admin/sale-channel", access: ["isAdmin"] },
            { key: "menu_admin_product", i18n: "menu.admin_product", path: "/admin/product", access: ["isAdmin"] },
            { key: "menu_admin_product_v2", i18n: "menu.admin_product_v2", path: "/admin/product_platform", access: ["isAdmin"] },
            { key: "menu_available_product", i18n: "wms.menu.available_product", path: "/wms/available-product", access: ["isAdmin"] },
            { key: "menu_admin_skurandom", i18n: "menu.admin_skurandom", path: "/admin/skurandom", access: ["isAdmin"] },
            { key: "menu_payment_channel", i18n: "payment_channel", path: "/admin/payment", access: ["isAdmin"] },
            { key: "menu_shipping_config", i18n: "shipping_config.menu", path: "/backoffice/shipping-config", access: ["isAdmin"] },
            { key: "menu_stock_forecast", i18n: "forecast.title", path: "/wms/forecast", access: ["isAdmin"] },
            { key: "menu_stock_usage", i18n: "usage.title", path: "/wms/usage", access: ["isAdmin"] }
        ]
    },
    {
        key: "menu_account",
        i18n: "account.name",
        path: "/account",
        access: ["isAccessAccount"],
        children: [
            { key: "menu_account_records", i18n: "account.menu.records", path: "/account/records", access: ["isAccessAccount"] },
            { key: "menu_account_items", i18n: "account.menu.items", path: "/account/items", access: ["isAccessAccount"] },
            { key: "menu_account_reports", i18n: "account.menu.reports", path: "/account/reports", access: ["isAccessAccount"] }
        ]
    },
    {
        key: "menu_member_admin",
        i18n: "member_admin.title",
        access: ["isAccessMember", "isAdmin"],
        children: [
            { key: "menu_member_admin_list", i18n: "member_admin.list_title", path: "/members", access: ["isAccessMember"] },
            { key: "menu_member_levels", i18n: "member_level.title", path: "/members/levels", access: ["isAdmin"] },
            { key: "menu_cod_rejections", i18n: "cod_rejections.menu", path: "/members/cod-rejections", access: ["isAdmin"] },
            { key: "menu_promo_notification", i18n: "promo_notification.title", path: "/notification/promo", access: ["isAdmin"] },
            // coin D3-4 — appended, never inserted: the "ตั้งค่าเมนู" preference
            // store keys off these node keys, and the sidebar in
            // DashboardLayout.vue mirrors this list in this order.
            { key: "menu_member_coins_report", i18n: "menu.member_coins_report", path: "/members/coins-report", access: ["isAccessMember"] }
        ]
    },
    {
        key: "menu_kol",
        i18n: "kol.name",
        path: "/kol",
        access: ["isAccessKOL"],
        children: [
            { key: "menu_kol_creators", i18n: "kol.menu.creators", path: "/kol/creators", access: ["isAccessKOL"] },
            { key: "menu_kol_assignments", i18n: "kol.menu.assignments", path: "/kol/assignments", access: ["isAccessKOL"] },
            { key: "menu_kol_reports", i18n: "kol.menu.reports", path: "/kol/reports", access: ["isAccessKOL"] },
            { key: "menu_kol_payout_batches", i18n: "kol.menu.payout_batches", path: "/kol/payout-batches", access: ["isAccessKOL"] }
        ]
    },
    {
        key: "menu_seller",
        i18n: "menu.seller",
        path: "/seller",
        access: ["isSeller", "isAdmin"],
        children: [
            // "สินค้า" (menu_app_catalog) and "ตั้งค่าร้านค้า" (menu_store_settings)
            // moved to the Seller Center app (mdf-seller-fn, seller.madamefin.com)
            // — owner 2026-10-20. Their routes stay in src/routes/routes.js so deep
            // links and taskbar tabs keep working; only the navigation entries
            // are gone. Stored menu prefs that still name those keys are inert:
            // nothing renders a key that is not in this tree.
            { key: "menu_seller_orders", i18n: "menu.seller_orders", path: "/seller/orders", access: ["isSeller", "isAdmin"] },
            { key: "menu_seller_payment_report", i18n: "menu.seller_payment_report", path: "/seller/payment-report", access: ["isSeller", "isAdmin"] }
        ]
    },
    {
        key: "menu_modern_trade",
        i18n: "modern_trade.name",
        path: "/modern-trade/companies",
        access: ["isAccessModernTrade"],
        children: [
            { key: "menu_mt_sale_orders", i18n: "modern_trade.menu.sale_orders", path: "/modern-trade/sale-orders", access: ["isAccessModernTrade"] },
            { key: "menu_mt_sales_import", i18n: "modern_trade.menu.sales_import", path: "/modern-trade/sales-import", access: ["isAccessModernTrade"] },
            { key: "menu_mt_consignment_payments", i18n: "modern_trade.menu.consignment_payments", path: "/modern-trade/consignment-payments", access: ["isAccessModernTrade"] },
            { key: "menu_mt_invoices", i18n: "modern_trade.menu.invoices", path: "/modern-trade/invoices", access: ["isAccessModernTrade"] },
            { key: "menu_mt_stock", i18n: "modern_trade.menu.stock", path: "/modern-trade/stock", access: ["isAccessModernTrade"] },
            { key: "menu_mt_audit_log", i18n: "modern_trade.menu.audit", path: "/modern-trade/audit-log", access: ["isAccessModernTrade"] },
            {
                key: "menu_mt_settings",
                i18n: "modern_trade.menu.settings",
                access: ["isAccessModernTrade"],
                children: [
                    { key: "menu_mt_products", i18n: "modern_trade.menu.products", path: "/modern-trade/products", access: ["isAccessModernTrade"] },
                    { key: "menu_mt_price_templates", i18n: "modern_trade.menu.price_templates", path: "/modern-trade/price-templates", access: ["isAccessModernTrade"] },
                    { key: "menu_mt_companies", i18n: "modern_trade.menu.companies", path: "/modern-trade/companies", access: ["isAccessModernTrade"] },
                    { key: "menu_mt_doc_format", i18n: "modern_trade.menu.doc_format", path: "/modern-trade/doc-format-settings", access: ["isAccessModernTrade"] }
                ]
            },
            {
                key: "menu_mt_reports",
                i18n: "modern_trade.menu.reports",
                access: ["isAccessModernTrade"],
                children: [
                    { key: "menu_mt_report_sales", i18n: "modern_trade.menu.report_sales", path: "/modern-trade/reports/sales", access: ["isAccessModernTrade"] },
                    { key: "menu_mt_report_stock_value", i18n: "modern_trade.menu.report_stock_value", path: "/modern-trade/reports/stock-value", access: ["isAccessModernTrade"] },
                    { key: "menu_mt_expiry_report", i18n: "modern_trade.menu.expiry_report", path: "/modern-trade/expiry-report", access: ["isAccessModernTrade"] },
                    { key: "menu_mt_expiry_returns", i18n: "modern_trade.menu.expiry_returns", path: "/modern-trade/expiry-returns", access: ["isAccessModernTrade"] }
                ]
            }
        ]
    },
    // #443 POS หน้าร้าน (spec 2026-10-10-pos-module-design §7). APPENDED at the end:
    // the keys above are the stable contract the menu preference store and
    // tests/unit/menuTreeI18n.spec.js hold. Supervisors see bills + reports;
    // the "manage" group is POS admin only.
    {
        key: "menu_pos",
        i18n: "pos.menu.root",
        path: "/pos/bills",
        access: ["isPosBO"],
        children: [
            { key: "menu_pos_bills", i18n: "pos.menu.bills", path: "/pos/bills", access: ["isPosBO"] },
            {
                key: "menu_pos_reports",
                i18n: "pos.menu.reports",
                access: ["isPosBO"],
                children: [
                    { key: "menu_pos_report_sales", i18n: "pos.menu.report_sales", path: "/pos/reports/sales", access: ["isPosBO"] },
                    { key: "menu_pos_report_top_spenders", i18n: "pos.menu.report_top_spenders", path: "/pos/reports/top-spenders", access: ["isPosBO"] },
                    { key: "menu_pos_report_products", i18n: "pos.menu.report_products", path: "/pos/reports/products", access: ["isPosBO"] },
                    { key: "menu_pos_report_promotions", i18n: "pos.menu.report_promotions", path: "/pos/reports/promotions", access: ["isPosBO"] },
                    // phase 3 — appended last (stored menu prefs are positional)
                    { key: "menu_pos_report_event_members", i18n: "pos.menu.report_event_members", path: "/pos/reports/event-members", access: ["isPosBO"] }
                ]
            },
            {
                key: "menu_pos_settings_group",
                i18n: "pos.menu.manage",
                access: ["isPosBOSupervisor"], // §13.1: pos_bo + level 2 opens the devices page
                children: [
                    { key: "menu_pos_devices", i18n: "pos.menu.devices", path: "/pos/devices", access: ["isPosBOSupervisor"] },
                    { key: "menu_pos_events", i18n: "pos.menu.events", path: "/pos/events", access: ["isPosBOAdmin"] },
                    // key kept (stored prefs); the page is POS products since phase 3
                    { key: "menu_pos_bundles", i18n: "pos.menu.products", path: "/pos/products", access: ["isPosBOAdmin"] },
                    { key: "menu_pos_settings", i18n: "pos.menu.settings", path: "/pos/settings", access: ["isPosBOAdmin"] }
                ]
            }
        ]
    },
    // การตลาด (Phase 1 ads §6.1) — APPENDED as the last node: menu prefs are
    // positional, never insert above existing nodes.
    {
        // Phase 3 §5.1: any-of — finance-only users must reach the payout pages.
        // pathFor: a finance-only user (no marketing role) would bounce off the
        // landing-page list, so the parent opens the affiliate members page.
        key: "menu_marketing", i18n: "marketing.menu.root", path: "/marketing/landing-pages", access: ["isAccessMarketing", "isAccessAffiliateFinance"],
        pathFor: { lacks: "isAccessMarketing", path: "/marketing/affiliate/members" },
        children: [
            { key: "menu_marketing_landing_pages", i18n: "marketing.menu.landing_pages", path: "/marketing/landing-pages", access: ["isAccessMarketing"] },
            { key: "menu_marketing_attribution", i18n: "marketing.menu.attribution", path: "/marketing/reports/attribution", access: ["isAccessMarketing"] },
            // Phase 2 referral (2026-10-13 §5.1) — appended at the end (prefs are positional).
            { key: "menu_marketing_referral_codes", i18n: "marketing.menu.referral_codes", path: "/marketing/referral-codes", access: ["isAccessMarketing"] },
            { key: "menu_marketing_referral_report", i18n: "marketing.menu.referral_report", path: "/marketing/reports/referral", access: ["isAccessMarketing"] },
            // Phase 3 affiliate (2026-10-13 §5.1) — appended at the end (prefs are positional).
            { key: "menu_marketing_affiliate_settings", i18n: "marketing.menu.affiliate_settings", path: "/marketing/affiliate/settings", access: ["isAccessMarketing"] },
            { key: "menu_marketing_affiliate_members", i18n: "marketing.menu.affiliate_members", path: "/marketing/affiliate/members", access: ["isAccessMarketing", "isAccessAffiliateFinance"] },
            { key: "menu_marketing_affiliate_commissions", i18n: "marketing.menu.affiliate_commissions", path: "/marketing/affiliate/commissions", access: ["isAccessMarketing", "isAccessAffiliateFinance"] },
            { key: "menu_marketing_affiliate_payouts", i18n: "marketing.menu.affiliate_payouts", path: "/marketing/affiliate/payouts", access: ["isAccessAffiliateFinance"] },
            { key: "menu_marketing_affiliate_report", i18n: "marketing.menu.affiliate_report", path: "/marketing/reports/affiliate", access: ["isAccessMarketing", "isAccessAffiliateFinance"] }
        ]
    }
];

/**
 * Returns true if the node is accessible to the current user, given a resolver
 * that maps an Auth getter name -> boolean (typically `n => rootGetters['Auth/'+n]`).
 * Empty access array => always accessible.
 */
export function isNodeAccessible(node, resolve) {
    if (!node.access || node.access.length === 0) return true;
    const has = g => {
        try {
            return !!resolve(g);
        } catch (e) {
            return false;
        }
    };
    // requireAll flips the array from OR to AND for the nodes whose v-if in
    // DashboardLayout.vue is `a && b` — see the NODE SHAPE note above.
    if (node.requireAll === true) return node.access.every(has);
    return node.access.some(has);
}

/**
 * Filters MENU_TREE down to nodes the current user may see, preserving the
 * tree shape. A parent is kept if it is itself accessible OR any descendant is.
 */
export function filterAccessibleTree(resolve, tree = MENU_TREE) {
    const out = [];
    tree.forEach(node => {
        const children = node.children ? filterAccessibleTree(resolve, node.children) : [];
        if (isNodeAccessible(node, resolve) || children.length > 0) {
            const clone = { key: node.key, i18n: node.i18n, access: node.access || [] };
            // requireAll travels with `access`: a clone that keeps the array but
            // drops the flag is silently re-read as OR by whoever calls
            // isNodeAccessible on the filtered tree.
            if (node.requireAll === true) clone.requireAll = true;
            if (node.path) clone.path = nodePath(node, resolve);
            if (children.length > 0) clone.children = children;
            out.push(clone);
        }
    });
    return out;
}

/** All menu keys, flattened (parents + descendants). */
export function allMenuKeys(tree = MENU_TREE) {
    const keys = [];
    tree.forEach(node => {
        keys.push(node.key);
        if (node.children) keys.push(...allMenuKeys(node.children));
    });
    return keys;
}

/**
 * The link of a node for the current user: `pathFor.path` when the user lacks
 * the `pathFor.lacks` getter (menu_marketing → affiliate members for a
 * finance-only user, Phase 3 verifier B #3), else `node.path`.
 */
export function nodePath(node, resolve) {
    if (!node) return "";
    const pf = node.pathFor;
    if (pf && pf.lacks) {
        let has = false;
        try {
            has = !!resolve(pf.lacks);
        } catch (e) {
            has = false;
        }
        if (!has) return pf.path;
    }
    return node.path;
}
